import { ArrowDownTrayIcon, ArrowLeftIcon, ClipboardDocumentIcon, EyeIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { Company } from '../../../@types/Company';
import { updateCompanyBranding } from '../../../store/AuthReducer';
import type { RootState } from '../../../store/store';
import { currentPlanningCycleYear, labelFromPositionHuman } from '../../../utils/boitageTimeline';
import { getValidReferents } from '../../../utils/branding';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';
import type { PreviewTarget } from '../../Setup/ContentChoice/PreviewModal';
import PreviewModal from '../../Setup/ContentChoice/PreviewModal';
import { downloadCustomDoc, getCustomDocForCampaign } from './utils/referentCampaignCustomDoc';
import { downloadReferentCampaignAsPDFOrZip } from './utils/referentCampaignPDF';
import type { ModelId, VersionId } from './utils/referentCampaignTemplates';
import {
  CUSTOM_MODEL_ID,
  CUSTOM_VERSION_ID,
  DEFAULT_REFERENT_CAMPAIGN_EMAIL_TEXT,
  extractModelContainerText,
  isCustomModelId,
  parseTemplateKey,
  renderModelContent,
  tabsConfig,
  versionLabels,
  versionsForModel,
  writeTextToClipboard,
} from './utils/referentCampaignTemplates';

type ViewId = 'content' | 'campaigns';

const VIEW_TABS: { id: ViewId; label: string }[] = [
  { id: 'campaigns', label: 'Campagne référent mutation location' },
  { id: 'content', label: 'Contenu' },
];

function ReferentCampaigns() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const company = useSelector((state: RootState) => state.auth.company) as Company;
  const jwt = useSelector((state: RootState) => state.auth.token);
  const validReferents = useMemo(() => getValidReferents(company), [company]);

  const steps = useMemo(() => tabsConfig, []);

  const defaultCampaign1 = parseTemplateKey(company?.brandingSettings?.defaultReferentCampaign1);
  const defaultCampaign2 = parseTemplateKey(company?.brandingSettings?.defaultReferentCampaign2);
  const defaultCampaign3 = parseTemplateKey(company?.brandingSettings?.defaultReferentCampaign3);

  const initialDefault = defaultCampaign1 ?? defaultCampaign2 ?? defaultCampaign3;
  const [activeModel, setActiveModel] = useState<ModelId>(initialDefault?.modelId ?? 'model1');
  const [activeVersions, setActiveVersions] = useState<Record<ModelId, VersionId>>(() => {
    const base: Record<ModelId, VersionId> = {
      model1: 'primary',
      model2: 'primary',
      model3: 'primary',
      model4: 'primary',
      model5: CUSTOM_VERSION_ID,
    };
    [defaultCampaign1, defaultCampaign2, defaultCampaign3].forEach((picked) => {
      if (picked) {
        base[picked.modelId] = picked.versionId;
      }
    });
    return base;
  });
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [downloadingCampaignNumber, setDownloadingCampaignNumber] = useState<number | null>(null);
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);

  const persistedEmailText = company?.brandingSettings?.referentCampaignEmailText ?? DEFAULT_REFERENT_CAMPAIGN_EMAIL_TEXT;
  const [emailText, setEmailText] = useState<string>(persistedEmailText);
  const [isSavingEmailText, setIsSavingEmailText] = useState(false);

  useEffect(() => {
    setEmailText(persistedEmailText);
  }, [persistedEmailText]);

  const isEmailTextDirty = emailText !== persistedEmailText;

  const activeVersion = activeVersions[activeModel];

  const activeView: ViewId = useMemo(() => {
    const requested = searchParams.get('view');
    return requested === 'content' ? 'content' : 'campaigns';
  }, [searchParams]);

  const handleSelectView = useCallback((view: ViewId) => {
    const next = new URLSearchParams(searchParams);
    if (view === 'campaigns') {
      next.delete('view');
    } else {
      next.set('view', view);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const timelineYear = useMemo(
    () => company?.toolsTimeline?.[String(currentPlanningCycleYear(company?.toolsTimeline))] || null,
    [company?.toolsTimeline],
  );

  useEffect(() => {
    setCopyState('idle');
  }, [activeModel, activeVersion]);

  const startMonth = useMemo(() => {
    const sm = typeof timelineYear?.startMonth === 'number' ? timelineYear?.startMonth : 9;
    return sm;
  }, [timelineYear?.startMonth]);

  const getDateFromPosition = (position: number): string => labelFromPositionHuman(startMonth, position);

  const defaultEventPositions = {
    referent: { 'Référent 1': 1.5, 'Référent 2': 7.5, 'Référent 3': 11 } as Record<string, number>,
  } as const;

  const defaultEventActivation = {
    referent: { 'Référent 1': true, 'Référent 2': true, 'Référent 3': false } as Record<string, boolean>,
  } as const;

  const safeEventPositions = useMemo(() => ({
    referent: { ...defaultEventPositions.referent, ...(timelineYear?.eventPositions?.referent ?? {}) },
  }), [timelineYear?.eventPositions]);

  const safeEventActivation = useMemo(() => ({
    referent: { ...defaultEventActivation.referent, ...(timelineYear?.eventActivation?.referent ?? {}) },
  }), [timelineYear?.eventActivation]);

  const planningCampaigns = useMemo(() => [
    {
      id: 'referent-1',
      number: 1,
      title: 'Première campagne',
      date: getDateFromPosition(safeEventPositions.referent['Référent 1']),
      isActive: safeEventActivation.referent['Référent 1'],
      defaultPick: defaultCampaign1,
    },
    {
      id: 'referent-2',
      number: 2,
      title: 'Deuxième campagne',
      date: getDateFromPosition(safeEventPositions.referent['Référent 2']),
      isActive: safeEventActivation.referent['Référent 2'],
      defaultPick: defaultCampaign2,
    },
    {
      id: 'referent-3',
      number: 3,
      title: 'Troisième campagne',
      date: getDateFromPosition(safeEventPositions.referent['Référent 3']),
      isActive: safeEventActivation.referent['Référent 3'],
      defaultPick: defaultCampaign3,
    },
  ], [safeEventPositions, safeEventActivation, defaultCampaign1, defaultCampaign2, defaultCampaign3]);

  const visiblePlanningCampaigns = useMemo(
    () => planningCampaigns.filter(c => c.isActive || c.number <= 2 || c.defaultPick),
    [planningCampaigns],
  );
  const activeCampaignCount = useMemo(
    () => planningCampaigns.filter(c => c.isActive).length,
    [planningCampaigns],
  );

  const handleGoBack = () => {
    try {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
      }
    } catch (error) {
      console.error('Navigation error :', error);
      navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
    }
  };

  const handleVersionChange = (modelId: ModelId, version: VersionId) => {
    setActiveVersions(prev => ({ ...prev, [modelId]: version }));
  };

  const applyCampaignPick = useCallback((pick: NonNullable<ReturnType<typeof parseTemplateKey>>) => {
    setActiveModel(pick.modelId);
    setActiveVersions(prev => ({ ...prev, [pick.modelId]: pick.versionId }));
  }, []);

  const copyModel = async(modelId: ModelId) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const version = activeVersions[modelId];
    const contentElement = document.getElementById(`${modelId}-${version}`);
    if (!contentElement) {
      setCopyState('error');
      return;
    }

    const textToCopy = extractModelContainerText(contentElement);

    try {
      await writeTextToClipboard(textToCopy);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch (error) {
      console.error('Copy failed :', error);
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 2000);
    }
  };

  const copyEmailText = async() => {
    if (!emailText) {
      showToast('error', 'Aucun texte à copier.');
      return;
    }
    try {
      await writeTextToClipboard(emailText);
      showToast('success', 'Texte copié dans le presse-papiers.');
    } catch (error) {
      console.error('Copy failed :', error);
      showToast('error', 'Impossible de copier le texte.');
    }
  };

  const handleSaveEmailText = async() => {
    if (!company?.id) {return;}
    setIsSavingEmailText(true);
    try {
      const payload = { referentCampaignEmailText: emailText };
      const response = await fetch(`/api/companies/${company.id}/branding-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({ brandingSettings: payload }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error((errorData && (errorData.error || errorData.message)) || 'Échec de la sauvegarde');
      }
      dispatch(updateCompanyBranding(payload));
      showToast('success', 'Texte du mail enregistré.');
    } catch (error) {
      console.error('Save referent email text failed:', error);
      showToast('error', "Impossible d'enregistrer le texte. Veuillez réessayer.");
    } finally {
      setIsSavingEmailText(false);
    }
  };

  const handleResetEmailText = () => {
    setEmailText(persistedEmailText);
  };

  const downloadModel = async(
    modelId: ModelId,
    version: VersionId,
    options?: { campaignNumber?: number; zipSlug?: string },
  ) => {
    if (options?.campaignNumber) {
      setDownloadingCampaignNumber(options.campaignNumber);
    } else {
      setIsDownloadingPdf(true);
    }
    try {
      if (isCustomModelId(modelId)) {
        const customDocs = company?.brandingSettings?.referentCampaignCustomDocs;
        const targetCampaign = (options?.campaignNumber as 1 | 2 | 3 | undefined)
          ?? (['1', '2', '3'] as const).find((key) => customDocs?.[key]);
        if (!targetCampaign) {
          showToast('error', 'Aucun document personnalisé importé.');
          return;
        }
        const campaignNumber = typeof targetCampaign === 'number'
          ? targetCampaign
          : Number(targetCampaign) as 1 | 2 | 3;
        const customDoc = getCustomDocForCampaign(customDocs, campaignNumber);
        if (!customDoc) {
          showToast('error', 'Aucun document personnalisé importé pour cette campagne.');
          return;
        }
        await downloadCustomDoc(company.id, campaignNumber, jwt, customDoc.filename);
        showToast('success', 'Document téléchargé.');
        return;
      }

      if (validReferents.length === 0) {
        showToast('error', 'Aucun référent valide configuré. Renseignez au moins un référent pour générer le PDF.');
        return;
      }
      await downloadReferentCampaignAsPDFOrZip({
        modelId,
        versionId: version,
        referents: validReferents,
        company,
        zipSlug: options?.zipSlug,
      });
      if (validReferents.length > 1) {
        showToast('success', `ZIP téléchargé (${validReferents.length} PDFs).`);
      } else {
        showToast('success', 'PDF téléchargé.');
      }
    } catch (error) {
      console.error('Referent campaign PDF download failed:', error);
      showToast('error', 'Une erreur est survenue lors du téléchargement.');
    } finally {
      setIsDownloadingPdf(false);
      setDownloadingCampaignNumber(null);
    }
  };

  const goToContentChoice = () => {
    navigate(`${ROUTES.NEW_DASHBOARD_PARAMETRAGE}?tab=contenu`);
  };

  const campaignDefaults: { number: number; pick: ReturnType<typeof parseTemplateKey> }[] = [
    { number: 1, pick: defaultCampaign1 },
    { number: 2, pick: defaultCampaign2 },
    { number: 3, pick: defaultCampaign3 },
  ];
  const matchedDefaultCampaigns = campaignDefaults.filter(({ pick }) => (
    pick && pick.modelId === activeModel && pick.versionId === activeVersion
  ));
  const hasAnyDefaultCampaign = Boolean(defaultCampaign1 || defaultCampaign2 || defaultCampaign3);

  // For the custom Modèle 5 placeholder we surface the first available
  // uploaded document (Contenu tab gives a global overview; the per-campaign
  // detail lives in the Campagne tab).
  const activeCustomDocPreview = useMemo(() => {
    if (!isCustomModelId(activeModel)) { return undefined; }
    const customDocs = company?.brandingSettings?.referentCampaignCustomDocs;
    const firstCampaignKey = (['1', '2', '3'] as const).find(key => customDocs?.[key]);
    if (!firstCampaignKey) { return undefined; }
    const campaignNumber = Number(firstCampaignKey) as 1 | 2 | 3;
    const doc = getCustomDocForCampaign(customDocs, campaignNumber);
    return doc ? { filename: doc.filename } : undefined;
  }, [activeModel, company?.brandingSettings?.referentCampaignCustomDocs]);

  const content = renderModelContent(activeModel, activeVersion, activeCustomDocPreview);

  const renderHeader = () => (
    <div className="mx-5 mt-5 rounded-2xl bg-rgpd-blue px-8 py-6 text-center text-white shadow-xl">
      <h1 className="text-xl font-bold tracking-tight">
        <span role="img" aria-label="clipboard" className="mr-2 align-middle">📋</span>
        MODULE 2 - COMMUNICATION RÉFÉRENT MUTATION LOCATION
      </h1>
      <p className="mx-auto mt-2 max-w-3xl text-base font-medium opacity-90">
        Les modèles de mails pour piloter vos campagnes de communication annuelles.
      </p>
    </div>
  );

  const renderViewTabs = () => (
    <div className="mx-5 mt-5">
      <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100 sm:grid-cols-2">
        {VIEW_TABS.map((tab) => {
          const isActive = activeView === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleSelectView(tab.id)}
              className={`px-6 py-4 text-center text-sm font-semibold tracking-wide uppercase transition-all ${
                isActive
                  ? 'border-b-4 border-blue-600 bg-white text-slate-800 shadow'
                  : 'border-b-4 border-transparent bg-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderEmailTextCard = () => (
    <div className="mx-5 mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-col gap-1">
        <h2 className="text-base font-semibold text-slate-800">Texte de l&apos;email</h2>
        <p className="text-xs text-slate-500">
          Ce texte sera rappelé sur l&apos;onglet « Campagne référent mutation location » pour rappel lors de l&apos;envoi.
          Il n&apos;est pas inclus dans le PDF généré.
        </p>
      </div>
      <textarea
        className="w-full rounded-lg border border-slate-300 p-3 text-sm text-slate-800 focus:border-rgpd-blue focus:ring-1 focus:ring-rgpd-blue focus:outline-none"
        rows={6}
        value={emailText}
        onChange={(event) => setEmailText(event.target.value)}
        placeholder="Saisissez ici le texte personnalisé à utiliser lors de l'envoi (objet, message d'accompagnement…)."
      />
      <div className="mt-3 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span className={`text-xs font-medium ${isEmailTextDirty ? 'text-orange-600' : 'text-emerald-600'}`}>
          {isEmailTextDirty ? 'Modifications non enregistrées' : 'Texte enregistré'}
        </span>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {isEmailTextDirty && (
            <button
              type="button"
              onClick={handleResetEmailText}
              disabled={isSavingEmailText}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Annuler
            </button>
          )}
          <button
            type="button"
            onClick={() => { void handleSaveEmailText(); }}
            disabled={!isEmailTextDirty || isSavingEmailText}
            className="rounded-lg bg-rgpd-blue px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rgpd-blue-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSavingEmailText ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderPlanningSummary = () => (
    <div className="mx-5 mt-6">
      <h2 className="mb-4 text-center text-lg font-semibold text-slate-800">
        <span role="img" aria-label="calendar" className="mr-2 align-middle">📅</span>
        Dates de communication planifiées
      </h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {visiblePlanningCampaigns.map((campaign) => {
          const pickedTab = campaign.defaultPick ? tabsConfig.find(t => t.id === campaign.defaultPick?.modelId) : null;
          const pickedVersion = campaign.defaultPick ? versionLabels[campaign.defaultPick.modelId]?.[campaign.defaultPick.versionId] : null;
          const pick = campaign.defaultPick;
          const isClickable = campaign.isActive && pick !== null && pick !== undefined;
          const isSelected = isClickable && pick.modelId === activeModel && pick.versionId === activeVersions[pick.modelId];

          const handleActivate = () => {
            if (isClickable) {
              applyCampaignPick(pick);
            }
          };

          return (
            <div
              key={campaign.id}
              aria-disabled={!campaign.isActive}
              aria-pressed={isClickable ? isSelected : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={isClickable ? handleActivate : undefined}
              onKeyDown={isClickable
                ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleActivate();
                  }
                }
                : undefined}
              title={isClickable ? `Sélectionner ${pickedTab?.title ?? ''} · ${pickedVersion?.label ?? ''}` : undefined}
              className={`relative rounded-lg border p-4 text-center transition-all ${
                campaign.isActive
                  ? isSelected
                    ? 'cursor-pointer border-blue-500 bg-blue-50 text-slate-800 ring-2 ring-blue-200'
                    : isClickable
                      ? 'cursor-pointer border-slate-200 bg-white text-slate-800 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md'
                      : 'border-slate-200 bg-white text-slate-800 hover:shadow-md'
                  : 'pointer-events-none border-dashed border-slate-300 bg-gray-50 text-slate-500 opacity-60'
              }`}
            >
              <div className="mb-1 text-2xl">Campagne {campaign.number}️</div>
              <div className={`mb-1 text-lg font-bold ${campaign.isActive ? 'text-slate-800' : 'text-slate-500'}`}>
                {campaign.date}
              </div>
              <div className="text-xs text-slate-600">{campaign.title}</div>
              {campaign.defaultPick && pickedTab && pickedVersion && (
                <div className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${
                  isSelected
                    ? 'border-blue-500 bg-blue-600 text-white'
                    : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}
                >
                  <span><span role="img" aria-label="emoji">★</span></span>
                  <span>Modèle préféré : {pickedTab.title} · {pickedVersion.label}</span>
                </div>
              )}
              {!campaign.isActive && (
                <div className="absolute -top-2 -right-2 z-10 rounded-full bg-gray-400 px-2 py-0.5 text-[10px] font-medium text-white shadow">Désactivée</div>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-center text-sm text-slate-600">
        Ces dates correspondent aux {activeCampaignCount} campagne{activeCampaignCount > 1 ? 's' : ''} annuelle{activeCampaignCount > 1 ? 's' : ''} de communication sur le Référent Mutation Location
      </p>
      {hasAnyDefaultCampaign ? (
        <p className="mt-2 text-center text-xs text-slate-500">
          <button
            type="button"
            onClick={goToContentChoice}
            className="text-rgpd-blue underline-offset-2 hover:underline"
          >
            Modifier les modèles préférés dans Paramétrage › Contenu croissance
          </button>
        </p>
      ) : (
        <p className="mt-2 text-center text-xs text-slate-500">
          Aucun modèle préféré défini —{' '}
          <button
            type="button"
            onClick={goToContentChoice}
            className="text-rgpd-blue underline-offset-2 hover:underline"
          >
            Configurer dans Paramétrage › Contenu croissance
          </button>
        </p>
      )}
    </div>
  );

  const renderContentView = () => (
    <>
      {renderEmailTextCard()}
      {renderPlanningSummary()}

      <div className="sticky top-5 z-20 mx-5 mt-6">
        <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100 lg:grid-cols-4">
          {steps.filter(step => !isCustomModelId(step.id)).map(step => {
            const isActive = activeModel === step.id;
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => setActiveModel(step.id)}
                className={`flex flex-col items-center justify-center gap-2 px-4 py-5 transition-all duration-300 hover:-translate-y-0.5 sm:px-6 ${
                  isActive
                    ? 'border-b-4 border-blue-600 bg-white font-bold text-slate-800 shadow'
                    : 'border-b-4 border-transparent bg-transparent font-semibold text-slate-500'
                }`}
              >
                {step.emoji !== null && step.emoji !== undefined && step.emoji !== '' && step.emoji}
                <span className="text-center text-sm tracking-wide uppercase">{step.badge}</span>
                <span className="text-center text-base font-semibold">{step.title}</span>
              </button>
            );
          })}
        </div>
        {(() => {
          const customStep = steps.find(step => isCustomModelId(step.id));
          if (!customStep) { return null; }
          const isActive = activeModel === customStep.id;
          return (
            <button
              type="button"
              onClick={() => setActiveModel(customStep.id)}
              className={`mt-2 flex w-full items-center justify-center gap-3 rounded-2xl border px-6 py-4 transition-all duration-300 hover:-translate-y-0.5 ${
                isActive
                  ? 'border-blue-600 bg-white font-bold text-slate-800 shadow'
                  : 'border-gray-200/80 bg-gray-100 font-semibold text-slate-500'
              }`}
            >
              {customStep.emoji !== null && customStep.emoji !== undefined && customStep.emoji !== '' && customStep.emoji}
              <span className="text-sm tracking-wide uppercase">{customStep.badge}</span>
              <span className="text-base font-semibold">{customStep.title}</span>
            </button>
          );
        })()}
      </div>

      <div className="px-5 pt-8 pb-16">
        <div className="rounded-3xl border border-slate-200 bg-slate-50/60 p-6 shadow-inner">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-3">
              {versionsForModel(activeModel).map(versionId => {
                const meta = versionLabels[activeModel]?.[versionId];
                if (!meta) { return null; }
                const isActive = activeVersion === versionId;
                return (
                  <button
                    key={versionId}
                    type="button"
                    onClick={() => handleVersionChange(activeModel, versionId)}
                    className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                      isActive
                        ? 'border-blue-600 bg-blue-600 text-white shadow focus:ring-blue-500'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700 focus:ring-blue-400'
                    }`}
                  >
                    {meta.icon !== null && meta.icon !== undefined && meta.icon !== '' && meta.icon}
                    <span>{meta.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              {activeModel !== CUSTOM_MODEL_ID && (
                <button
                  type="button"
                  onClick={() => copyModel(activeModel)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-rgpd-blue px-4 py-2 text-sm font-semibold text-white shadow"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                  <span>Copier le contenu</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => { void downloadModel(activeModel, activeVersion); }}
                disabled={
                  isCustomModelId(activeModel)
                    ? !activeCustomDocPreview
                    : isDownloadingPdf || validReferents.length === 0
                }
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-rgpd-blue bg-white px-4 py-2 text-sm font-semibold text-rgpd-blue shadow transition-colors hover:bg-rgpd-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                <span>
                  {isCustomModelId(activeModel)
                    ? activeCustomDocPreview
                      ? 'Télécharger le document'
                      : 'Aucun document importé'
                    : isDownloadingPdf
                      ? 'Génération…'
                      : validReferents.length > 1
                        ? `Télécharger en ZIP (${validReferents.length} PDFs)`
                        : 'Télécharger en PDF'}
                </span>
              </button>
            </div>
          </div>
          {copyState !== 'idle' && (
            <div
              role="status"
              aria-live="polite"
              className={`mt-2 text-sm font-medium ${copyState === 'copied' ? 'text-green-600' : 'text-red-600'}`}
            >
              {copyState === 'copied'
                ? 'Contenu copié dans le presse-papiers.'
                : 'Impossible de copier le contenu.'}
            </div>
          )}
          {matchedDefaultCampaigns.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              {matchedDefaultCampaigns.map(({ number }) => (
                <span
                  key={number}
                  className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-semibold text-blue-700"
                >
                  <span><span role="img" aria-label="emoji">★</span></span>
                  <span>Modèle préféré pour Campagne {number}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 space-y-6">{content}</div>
      </div>
    </>
  );

  const renderCampaignsView = () => (
    <div className="px-5 pt-8 pb-16">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-slate-800">Texte de l&apos;email — rappel</h2>
        <p className="mt-1 text-xs text-slate-500">
          Texte personnalisé saisi sur l&apos;onglet « Contenu ». Il sert de modèle de copier-coller pour vos envois.
        </p>
        <textarea
          className="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-700"
          rows={6}
          value={persistedEmailText}
          readOnly
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => { void copyEmailText(); }}
            className="inline-flex items-center gap-2 rounded-lg border border-rgpd-blue px-3 py-1.5 text-xs font-semibold text-rgpd-blue transition-colors hover:bg-rgpd-blue hover:text-white"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copier le texte
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {planningCampaigns.map((campaign) => {
          const pickedTab = campaign.defaultPick ? tabsConfig.find(t => t.id === campaign.defaultPick?.modelId) : null;
          const pickedVersion = campaign.defaultPick ? versionLabels[campaign.defaultPick.modelId]?.[campaign.defaultPick.versionId] : null;
          const isDownloading = downloadingCampaignNumber === campaign.number;

          return (
            <div
              key={campaign.id}
              className={`rounded-2xl border p-5 shadow-sm transition-all ${
                campaign.isActive
                  ? 'border-slate-200 bg-white'
                  : 'border-dashed border-slate-300 bg-gray-50 opacity-80'
              }`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-rgpd-blue/10 px-3 py-1 text-xs font-bold tracking-wide text-rgpd-blue uppercase">
                      Campagne {campaign.number}
                    </span>
                    <span className="text-sm font-semibold text-slate-800">{campaign.title}</span>
                    {!campaign.isActive && (
                      <span className="rounded-full bg-gray-400 px-2 py-0.5 text-[10px] font-medium text-white">
                        Désactivée
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    <span role="img" aria-label="calendar" className="mr-1.5">📅</span>
                    Date prévue : <span className="font-semibold text-slate-800">{campaign.date}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-600">
                    {campaign.defaultPick && pickedTab && pickedVersion ? (
                      <>
                        <span role="img" aria-label="email" className="mr-1.5">✉️</span>
                        Modèle : <span className="font-semibold text-slate-800">{pickedTab.title}</span>
                        {' · '}
                        <span className="font-semibold text-slate-800">{pickedVersion.label}</span>
                      </>
                    ) : (
                      <span className="text-amber-700">
                        Aucun modèle préféré défini —{' '}
                        <button
                          type="button"
                          onClick={goToContentChoice}
                          className="text-rgpd-blue underline-offset-2 hover:underline"
                        >
                          Configurer dans Paramétrage › Contenu croissance
                        </button>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
                  {campaign.defaultPick && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          if (!campaign.defaultPick) {return;}
                          void downloadModel(campaign.defaultPick.modelId, campaign.defaultPick.versionId, {
                            campaignNumber: campaign.number,
                            zipSlug: `campagne-${campaign.number}`,
                          });
                        }}
                        disabled={
                          isDownloading
                          || (
                            isCustomModelId(campaign.defaultPick.modelId)
                              ? !getCustomDocForCampaign(company?.brandingSettings?.referentCampaignCustomDocs, campaign.number as 1 | 2 | 3)
                              : validReferents.length === 0
                          )
                        }
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-rgpd-blue bg-white px-4 py-2 text-sm font-semibold text-rgpd-blue shadow-sm transition-colors hover:bg-rgpd-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                        <span>
                          {isDownloading
                            ? 'Génération…'
                            : isCustomModelId(campaign.defaultPick.modelId)
                              ? 'Télécharger le document'
                              : validReferents.length > 1
                                ? `Télécharger en ZIP (${validReferents.length} PDFs)`
                                : 'Télécharger en PDF'}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!campaign.defaultPick) {return;}
                          const customDoc = isCustomModelId(campaign.defaultPick.modelId)
                            ? getCustomDocForCampaign(company?.brandingSettings?.referentCampaignCustomDocs, campaign.number as 1 | 2 | 3)
                            : null;
                          setPreviewTarget({
                            kind: 'referent',
                            modelId: campaign.defaultPick.modelId,
                            versionId: campaign.defaultPick.versionId,
                            customDocFilename: customDoc?.filename,
                          });
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-rgpd-blue hover:text-rgpd-blue"
                      >
                        <EyeIcon className="h-5 w-5" />
                        <span>Voir le contenu</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {campaign.defaultPick && (
                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <div className="mb-1 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                    Texte de l&apos;email à coller
                  </div>
                  <textarea
                    className="w-full rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700"
                    rows={4}
                    value={persistedEmailText}
                    readOnly
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="rounded-lg bg-white p-8 shadow-md">
      <div className="mb-8 flex items-center">
        <button
          type="button"
          onClick={handleGoBack}
          className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
      </div>

      <div className="min-h-screen leading-relaxed text-slate-800">
        {renderHeader()}
        {renderViewTabs()}
        {activeView === 'content' ? renderContentView() : renderCampaignsView()}
      </div>

      <PreviewModal
        isOpen={previewTarget !== null}
        target={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />
    </div>
  );
}

export default ReferentCampaigns;
